import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useForm, Controller } from 'react-hook-form';
import Button from '@ui-elements/Button';
import InputWithLabel from '@ui-parts/InputWithLabel';
import { PALETTE, ADMIN_PALETTE, TYPOGRAPHY } from '~/assets/styles/base';
import { ROUTES } from '~/constants/routes';
import {
  passwordValidationRule,
  adminEmailValidationRule,
  adminNameValidationRule,
} from '~/constants/validationRules';
import { ADMIN_ROLES } from '~/common/constants';
import { LOCAL_STORAGES } from '~/constants';

const StyledForm = styled.form`
  background: ${PALETTE.white};
  width: 720px;
  border: 1px solid ${PALETTE.border.frame};
  margin: 55px auto;
  padding: 55px 90px;
`;

const ControlWrapper = styled.div`
  margin-bottom: 20px;
`;

const CenterBox = styled.div`
  width: 455px;
  margin: 0 auto;
`;

function AdminSignup() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const admin = useSelector((state) => state.admin);

  const { control, handleSubmit, setValue, getValues } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
      name: '',
    },
  });

  const onSubmit = (data) => {
    dispatch({
      type: 'admin/change',
      payload: {
        admin: {
          email: data.email,
          password: data.password,
          confirmPassword: data.confirmPassword,
          name: data.name,
        },
      },
    });
    navigate(ROUTES.ADMIN.SIGNUP_CONFIRM);
  };

  useEffect(() => {
    // Check admin role ACCOUNT_MANAGEMENT_ROLE
    // - If account has not permission => navigate to 403 page.
    const adminRoles = localStorage.getItem(LOCAL_STORAGES.ADMIN_ROLES)?.split(',');
    if (!(adminRoles && adminRoles.includes(ADMIN_ROLES.ACCOUNT_MANAGEMENT_ROLE.toString()))) {
      navigate(ROUTES.ADMIN[403]);
    }
    setValue('name', admin.name);
    setValue('password', admin.password);
    setValue('confirmPassword', admin.confirmPassword);
    setValue('email', admin.email);
  }, []);

  return (
    <StyledForm onSubmit={handleSubmit(onSubmit)}>
      <Typography
        sx={{
          color: ADMIN_PALETTE.primary.main,
          fontSize: TYPOGRAPHY.title1,
          textAlign: 'center',
          marginBottom: '10px',
        }}
        data-testid="pages-adminsignup-title"
      >
        {t('registration.adminTitle')}
      </Typography>
      <CenterBox>
        <ControlWrapper>
          <Controller
            name="email"
            control={control}
            rules={adminEmailValidationRule}
            render={({ field: { onChange, value, name, ref }, fieldState: { error } }) => (
              <InputWithLabel
                label={t('registration.email')}
                name={name}
                value={value}
                handleChange={(e) => {
                  onChange(e.target.value);
                }}
                handleBlur={(e) => {
                  setValue(name, e.target.value.trim());
                }}
                handleOnKeyDown={(e) => {
                  if (e.keyCode === 13) {
                    setValue(name, e.target.value.trim());
                  }
                }}
                error={Boolean(error)}
                errorMessage={error?.message}
                dataTestId="pages-adminsignup-email"
                required
                width="455px"
                inputProps={{ maxLength: 100 }}
                ref={ref}
              />
            )}
          />
        </ControlWrapper>
        <ControlWrapper>
          <Controller
            name="password"
            control={control}
            rules={passwordValidationRule}
            render={({ field: { onChange, value, name, ref }, fieldState: { error } }) => (
              <InputWithLabel
                type="password"
                label={t('registration.password')}
                name={name}
                value={value}
                handleChange={(e) => {
                  onChange(e.target.value);
                }}
                handleBlur={(e) => {
                  setValue(name, e.target.value.trim());
                }}
                handleOnKeyDown={(e) => {
                  if (e.keyCode === 13) {
                    setValue(name, e.target.value.trim());
                  }
                }}
                error={Boolean(error)}
                errorMessage={error?.message}
                dataTestId="pages-adminsignup-password"
                required
                width="455px"
                description={t('registration.passwordNote')}
                background={PALETTE.bg.main}
                fontSize="note2"
                padding="15px 10px"
                inputProps={{ maxLength: 24 }}
                ref={ref}
              />
            )}
          />
        </ControlWrapper>
        <ControlWrapper>
          <Controller
            name="confirmPassword"
            control={control}
            rules={{
              ...passwordValidationRule,
              validate: {
                message: (v) => (v === getValues('password') ? null : t('validation.pswNotSame')),
              },
            }}
            render={({ field: { onChange, value, name, ref }, fieldState: { error } }) => (
              <InputWithLabel
                type="password"
                label={t('registration.confirmPassword')}
                name={name}
                value={value}
                handleChange={(e) => {
                  onChange(e.target.value);
                }}
                handleBlur={(e) => {
                  setValue(name, e.target.value.trim());
                }}
                handleOnKeyDown={(e) => {
                  if (e.keyCode === 13) {
                    setValue(name, e.target.value.trim());
                  }
                }}
                error={Boolean(error)}
                errorMessage={error?.message}
                dataTestId="pages-adminsignup-confirmpassword"
                required
                width="455px"
                inputProps={{ maxLength: 24 }}
                ref={ref}
              />
            )}
          />
        </ControlWrapper>
        <ControlWrapper>
          <Controller
            name="name"
            control={control}
            rules={adminNameValidationRule}
            render={({ field: { onChange, value, name, ref }, fieldState: { error } }) => (
              <InputWithLabel
                label={t('registration.name')}
                name={name}
                value={value}
                handleChange={(e) => {
                  onChange(e.target.value);
                }}
                handleBlur={(e) => {
                  setValue(name, e.target.value.trim());
                }}
                handleOnKeyDown={(e) => {
                  if (e.keyCode === 13) {
                    setValue(name, e.target.value.trim());
                  }
                }}
                error={Boolean(error)}
                errorMessage={error?.message}
                dataTestId="pages-adminsignup-name"
                required
                width="455px"
                inputProps={{ maxLength: 20 }}
                ref={ref}
              />
            )}
          />
        </ControlWrapper>
      </CenterBox>
      <Box sx={{ textAlign: 'center', marginTop: '55px' }}>
        <Button
          type="submit"
          variant="contained"
          data-testid="pages-adminsignup-confirmButton"
          sx={{ width: 148 }}
        >
          {t('appCommon.confirm')}
        </Button>
      </Box>
    </StyledForm>
  );
}

export default AdminSignup;
